import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/apiClient';
import { useAuth } from '@/context/AuthContext';

export type HealthStatus = 'healthy' | 'needs_attention' | 'at_risk';
export type SortOrder = 'name_asc' | 'name_desc';
export type AttendanceStatus = 'active' | 'inactive';

export interface RosterMember {
  id: string;
  displayName: string;
  firstName: string | null;
  lastName: string | null;
  email: string;
  phone: string | null;
  profileImageUrl: string | null;
  membershipStatus: string | null;
  joinedAt: string;
  // Leader-only fields (null for regular members)
  healthStatus?: HealthStatus | null;
  healthScore?: number | null;
  attendanceRate?: number | null;
  attendanceCount?: number | null;
  consecutiveAbsences?: number | null;
  lastAttendedAt?: string | null;
  howFoundChurch?: string | null;
}

export interface RosterFilters {
  search?: string;
  sort?: SortOrder;
  healthStatus?: HealthStatus;
  membershipStatusId?: string;
  attendanceStatus?: AttendanceStatus;
  joinedAfter?: string;
  joinedBefore?: string;
}

interface RosterApiResponse {
  success: boolean;
  data: {
    members: RosterMember[];
    total: number;
  };
  meta: {
    isLeader: boolean;
    availableFilters: string[];
  };
}

/**
 * Fetch roster (member list) for a group
 * Leaders see additional fields (health status, attendance metrics)
 * Members see basic contact info only
 */
export function useRoster(groupId: string | undefined, filters: RosterFilters = {}) {
  const { currentGroup, isSwitchingOrg } = useAuth();
  const activeGroupId = groupId || currentGroup?.id;

  return useQuery({
    queryKey: ['roster', activeGroupId, filters],
    queryFn: async (): Promise<{
      members: RosterMember[];
      isLeader: boolean;
      availableFilters: string[];
    }> => {
      const params = new URLSearchParams();
      params.append('groupId', activeGroupId!);

      if (filters.search) params.append('search', filters.search);
      if (filters.sort) params.append('sort', filters.sort);
      if (filters.healthStatus) params.append('healthStatus', filters.healthStatus);
      if (filters.membershipStatusId) params.append('membershipStatusId', filters.membershipStatusId);
      if (filters.attendanceStatus) params.append('attendanceStatus', filters.attendanceStatus);
      if (filters.joinedAfter) params.append('joinedAfter', filters.joinedAfter);
      if (filters.joinedBefore) params.append('joinedBefore', filters.joinedBefore);

      const response = await api.get<RosterApiResponse>(`/roster?${params.toString()}`);

      return {
        members: response.data?.members ?? [],
        isLeader: response.meta?.isLeader ?? false,
        availableFilters: response.meta?.availableFilters ?? ['search', 'sort'],
      };
    },
    // Gate on !isSwitchingOrg so refetches during the 1.5–7.5s claims-
    // verification window don't fire with NEW_TOKEN + OLD_GROUP_ID and
    // 403 against the new org's RLS context.
    enabled: !!activeGroupId && !isSwitchingOrg,
    staleTime: 2 * 60 * 1000, // 2 minutes - roster changes more frequently
  });
}

/**
 * Get display name for a roster member
 * Uses displayName from backend (already computed as preferred_name or first+last)
 */
export function getDisplayName(member: RosterMember): string {
  return member.displayName || 'Unknown';
}

/**
 * Get initials for avatar fallback
 */
export function getInitials(member: RosterMember): string {
  const displayName = getDisplayName(member);
  const parts = displayName.split(' ').filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  }
  return displayName.substring(0, 2).toUpperCase();
}
